import type {
  AppType,
  ConsentStatus,
  ConsentTileConfig,
  StepConfig
} from './MsDefenderIntegration.types';
import {
  AppCategory,
  AppType as AppTypeEnum,
  ConsentStatus as ConsentStatusEnum
} from './MsDefenderIntegration.types';

export const INGEST_CASCADE: Partial<Record<AppType, AppType[]>> = {
  [AppTypeEnum.ENTRA_AND_DEFENDER_LOG_INGESTION]: [
    AppTypeEnum.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS
  ]
};

// Response-action types that target a device (Defender host isolation) rather than an identity.
export const MICROSOFT_DEVICE_ACTION_TYPES = new Set([
  'microsoft_isolate_host',
  'microsoft_release_host_from_isolation'
]);

export const REQUESTED_SCOPES: Record<AppType, string[]> = {
  [AppTypeEnum.ENTRA_IDENTITY_RESPONSE_ACTIONS]: [
    'Organization.Read.All',
    'SecurityAlert.ReadWrite.All',
    'SecurityEvents.ReadWrite.All',
    'SecurityIncident.ReadWrite.All',
    'User.ManageIdentities.All',
    'User.Read.All',
    'User.Read',
    'UserAuthenticationMethod.ReadWrite.All'
  ],
  [AppTypeEnum.ENTRA_IDENTITY_LOG_INGESTION]: [
    'AuditLog.Read.All',
    'Directory.Read.All',
    'IdentityRiskEvent.Read.All',
    'IdentityRiskyServicePrincipal.Read.All',
    'IdentityRiskyUser.Read.All',
    'Organization.Read.All',
    'SecurityAlert.Read.All',
    'SecurityEvents.Read.All',
    'SecurityIncident.Read.All',
    'ActivityFeed.Read',
    'ActivityFeed.ReadDlp',
    'ServiceHealth.Read'
  ],
  [AppTypeEnum.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS]: [
    'AdvancedQuery.Read.All',
    'Alert.ReadWrite.All',
    'Machine.CollectForensics',
    'Machine.Isolate',
    'Machine.Read.All',
    'Machine.Scan',
    'Machine.StopAndQuarantine',
    'Organization.Read.All',
    'SecurityAlert.ReadWrite.All',
    'SecurityEvents.ReadWrite.All',
    'SecurityIncident.ReadWrite.All',
    'Software.Read.All',
    'Ti.Read.All',
    'User.ManageIdentities.All',
    'User.Read.All',
    'UserAuthenticationMethod.ReadWrite.All',
    'Vulnerability.Read.All'
  ],
  [AppTypeEnum.ENTRA_AND_DEFENDER_LOG_INGESTION]: [
    'AuditLog.Read.All',
    'Directory.Read.All',
    'IdentityRiskEvent.Read.All',
    'IdentityRiskyServicePrincipal.Read.All',
    'IdentityRiskyUser.Read.All',
    'Organization.Read.All',
    'SecurityAlert.Read.All',
    'SecurityEvents.Read.All',
    'SecurityIncident.Read.All',
    'ActivityFeed.Read',
    'ActivityFeed.ReadDlp',
    'ServiceHealth.Read',
    'Alert.Read.All'
  ],
  [AppTypeEnum.ENTRA_IDENTITY_INVENTORY_READ]: [
    'Device.Read.All',
    'DeviceManagementManagedDevices.Read.All',
    'Machine.Read.All'
  ]
};

export const MICROSOFT_365_INGESTION: ConsentTileConfig = {
  appType: AppTypeEnum.ENTRA_IDENTITY_LOG_INGESTION,
  category: AppCategory.INGESTION,
  title: 'Microsoft 365 Ingestion',
  description:
    'Microsoft 365 audit, identity, and security telemetry. No Defender data.',
  alternativeGuidance:
    'Choose this option to ingest Microsoft 365 data without Microsoft Defender data.',
  capabilities: [
    'Microsoft 365 audit and security telemetry data',
    'Microsoft Entra ID identity telemetry data',
    'NOT Defender alerts and device telemetry data - Not Included'
  ],
  requestedScopes: REQUESTED_SCOPES[AppTypeEnum.ENTRA_IDENTITY_LOG_INGESTION]
};

export const DEFENDER_INGESTION: ConsentTileConfig = {
  appType: AppTypeEnum.ENTRA_AND_DEFENDER_LOG_INGESTION,
  category: AppCategory.INGESTION,
  title: 'Microsoft 365 + Microsoft Defender Ingestion',
  description:
    'Everything in Microsoft 365 Ingestion, plus supported alerts and security data from Microsoft Defender products.',
  alternativeGuidance:
    'Choose this option to ingest Microsoft 365 data plus supported Microsoft Defender data.',
  capabilities: [
    'Microsoft 365 audit and security data',
    'Microsoft Entra ID identity data',
    'Microsoft Defender alerts and security data, including Defender for Endpoint'
  ],
  requestedScopes:
    REQUESTED_SCOPES[AppTypeEnum.ENTRA_AND_DEFENDER_LOG_INGESTION]
};

export const MUTUALLY_EXCLUSIVE_APP_CATEGORIES: AppType[][] = [
  [
    AppTypeEnum.ENTRA_IDENTITY_LOG_INGESTION,
    AppTypeEnum.ENTRA_AND_DEFENDER_LOG_INGESTION
  ],
  [
    AppTypeEnum.ENTRA_IDENTITY_RESPONSE_ACTIONS,
    AppTypeEnum.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS
  ]
];

export const ENTRA_RESPONSE: ConsentTileConfig = {
  appType: AppTypeEnum.ENTRA_IDENTITY_RESPONSE_ACTIONS,
  category: AppCategory.RESPONSE,
  title: 'Microsoft Entra ID Response Actions',
  description:
    'Lets Pondurance revoke sessions or reset a password in Microsoft Entra ID. No device actions.',
  alternativeGuidance:
    'Choose this instead if identity response actions are allowed but device isolation is not. Identity response can be enabled without host isolation.',
  capabilities: [
    'Microsoft Entra ID containment actions',
    'NOT Defender for Endpoint device isolation - Not Included'
  ],
  requestedScopes: REQUESTED_SCOPES[AppTypeEnum.ENTRA_IDENTITY_RESPONSE_ACTIONS]
};

export const DEFENDER_RESPONSE: ConsentTileConfig = {
  appType: AppTypeEnum.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS,
  category: AppCategory.RESPONSE,
  title: 'Microsoft Entra ID + Defender for Endpoint Response Actions',
  description:
    'Everything in Microsoft Entra ID Response Actions, plus isolating a device with Defender for Endpoint.',
  alternativeGuidance:
    'Choose this instead if Pondurance may take Entra identity actions and isolate Defender for Endpoint devices.',
  capabilities: [
    'Microsoft Entra ID containment actions',
    'Defender for Endpoint device isolation'
  ],
  requestedScopes:
    REQUESTED_SCOPES[AppTypeEnum.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS],
  requirementNote:
    'Requires the Microsoft 365 + Microsoft Defender Ingestion app on the same tenant.'
};

export const ASSET_INVENTORY_TILE: ConsentTileConfig = {
  appType: AppTypeEnum.ENTRA_IDENTITY_INVENTORY_READ,
  category: AppCategory.INVENTORY,
  title: 'Microsoft Defender for Endpoint Asset Inventory',
  description:
    'Connect your tenants to allow Pondurance to read Defender for Endpoint device and asset inventory.',
  capabilities: ['Defender for Endpoint device and asset inventory'],
  requestedScopes: REQUESTED_SCOPES[AppTypeEnum.ENTRA_IDENTITY_INVENTORY_READ]
};

export const MICROSOFT_STEPS: StepConfig[] = [
  {
    category: AppCategory.INGESTION,
    label: 'DATA INGESTION',
    heading: 'Choose the telemetry Pondurance ingests',
    instruction:
      "Select one ingestion option per tenant. This is the data Pondurance monitors and alerts on, and it's required before response actions can be enabled.",
    options: [MICROSOFT_365_INGESTION, DEFENDER_INGESTION]
  },
  {
    category: AppCategory.RESPONSE,
    label: 'RESPONSE ACTIONS',
    heading: 'Choose which actions Pondurance can take',
    instruction:
      'Optional. Identity actions include revoking sessions and resetting a password. Device isolation requires Defender for Endpoint.',
    options: [ENTRA_RESPONSE, DEFENDER_RESPONSE]
  }
];

export const ALL_TILES: ConsentTileConfig[] = [
  ...MICROSOFT_STEPS.flatMap((step) => step.options),
  ASSET_INVENTORY_TILE
];

export const TOKEN_STATUS_LABEL: Record<ConsentStatus, string> = {
  [ConsentStatusEnum.CONNECTED]: 'Connected',
  [ConsentStatusEnum.ERROR]: 'Reauthorization needed',
  [ConsentStatusEnum.NOT_CONNECTED]: 'Not connected',
  [ConsentStatusEnum.INSUFFICIENT_PERMISSIONS]: 'Permissions required'
};

export const LAST_ACTION_FAILED_LABEL = 'Last action failed';

export const PREREQUISITE_MISSING_LABEL = 'Prerequisite missing';

export const PREREQUISITE_MISSING_MESSAGE = `This tenant's ${DEFENDER_INGESTION.title} app is disconnected, so device isolation is unavailable. Reconnect it to restore full response actions, or switch this tenant to ${ENTRA_RESPONSE.title} for identity actions only.`;
